import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { useAppContext } from './useAppContext';
import { API_BASE_URL, BILLING_POLL_INTERVAL } from '../constants';
import { safeParseNumber } from '../types';
import type {
  BillingBalance,
  BillingHistory,
  SetupIntentResponse,
  PaymentMethod,
  PayAsYouGoStatus,
  PayAsYouGoUnlockRequest,
  PayAsYouGoUnlockResponse,
  PayAsYouGoBatchCalculation
} from '../types';

export const useBilling = () => {
  const { apiKey } = useAuth();
  const { dispatch, showToast } = useAppContext();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchBalance = useCallback(async () => {
    if (!apiKey) return;

    dispatch({ type: 'SET_BILLING_LOADING', payload: true });
    try {
      // Get basic balance info
      const balanceResponse = await fetch(`${API_BASE_URL}/api/billing/balance`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });

      if (!balanceResponse.ok) {
        if (balanceResponse.status === 401) {

          localStorage.setItem('show_login_page', 'true');
          window.location.reload();
          return;
        }
        throw new Error(`Failed to fetch balance: ${balanceResponse.status} ${balanceResponse.statusText}`);
      }
      
      const balanceData: BillingBalance = await balanceResponse.json();
      
      // Use balance data directly - don't override with job calculations
      const data: BillingBalance = balanceData;
      
      // ENHANCED: Validate that we got actual data
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid billing data received from server');
      }

      // ENHANCED: Validate balance is parseable with better error handling
      const numericBalance = safeParseNumber(data.balance);
      if (data.balance && isNaN(numericBalance)) {

        // Don't throw error, just log warning and continue with 0
      }

      dispatch({ type: 'SET_BILLING_BALANCE', payload: data });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch balance';
      console.error('❌ Billing fetch error:', err);
      dispatch({ type: 'SET_BILLING_ERROR', payload: errorMessage });
      showToast({ 
        type: 'error', 
        title: 'Balance Error', 
        message: 'Failed to fetch balance data' 
      });
    } finally {
      dispatch({ type: 'SET_BILLING_LOADING', payload: false });
    }
  }, [apiKey, dispatch, showToast]);

  const createSetupIntent = useCallback(async (): Promise<SetupIntentResponse | null> => {
    if (!apiKey) return null;

    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/create-setup-intent`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });

      if (!response.ok) {
        if (response.status === 401) {

          localStorage.setItem('show_login_page', 'true');
          window.location.reload();
          return null;
        }
        throw new Error(`Failed to create setup intent: ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create setup intent';
      showToast({ type: 'error', title: 'Setup Error', message: errorMessage });
      return null;
    }
  }, [apiKey, showToast]);

  const confirmSetupIntent = useCallback(async (
    setupIntentId: string, 
    paymentMethodId: string
  ): Promise<boolean> => {
    if (!apiKey) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/confirm-setup-intent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          setup_intent_id: setupIntentId,
          payment_method_id: paymentMethodId
        })
      });

      if (!response.ok) {
        if (response.status === 401) {

          localStorage.setItem('show_login_page', 'true');
          window.location.reload();
          return false;
        }
        throw new Error(`Failed to confirm setup intent: ${response.status}`);
      }
      
      showToast({ type: 'success', title: 'Success', message: 'Payment method saved' });
      await fetchBalance();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to confirm setup intent';
      showToast({ type: 'error', title: 'Error', message: errorMessage });
      return false;
    }
  }, [apiKey, showToast, fetchBalance]);

  const toggleAutoCharge = useCallback(async (enabled: boolean): Promise<boolean> => {
    if (!apiKey) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/auto-charge`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled })
      });

      if (!response.ok) {
        if (response.status === 401) {

          localStorage.setItem('show_login_page', 'true');
          window.location.reload();
          return false;
        }
        throw new Error(`Failed to update auto-charge: ${response.status}`);
      }
      
      showToast({ 
        type: 'success', 
        title: 'Setting Updated', 
        message: enabled ? 'Auto-charge enabled' : 'Auto-charge disabled'
      });
      await fetchBalance();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update auto-charge';
      showToast({ type: 'error', title: 'Error', message: errorMessage });
      return false;
    }
  }, [apiKey, showToast, fetchBalance]);

  const getBillingHistory = useCallback(async (
    page = 1, 
    size = 20
  ): Promise<BillingHistory | null> => {
    if (!apiKey) return null;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/billing/history?page=${page}&size=${size}`,
        { headers: { 'Authorization': `Bearer ${apiKey}` } }
      );

      if (!response.ok) {
        if (response.status === 401) {

          localStorage.setItem('show_login_page', 'true');
          window.location.reload();
          return null;
        }
        throw new Error(`Failed to fetch history: ${response.status}`);
      }
      return await response.json();
    } catch {
      showToast({ type: 'error', title: 'Error', message: 'Failed to load history' });
      return null;
    }
  }, [apiKey, showToast]);

  const getMonthlyUsage = useCallback(async (): Promise<{ amount: number; contacts: number } | null> => {
    if (!apiKey) return null;
    try {
        const response = await fetch(`${API_BASE_URL}/api/billing/summary`, {
            headers: { 'Authorization': `Bearer ${apiKey}` }
        });
        if (!response.ok) {
            if (response.status === 401) {

              localStorage.setItem('show_login_page', 'true');
              window.location.reload();
              return null;
            }
            throw new Error('Failed to fetch billing summary');
        }
        const data = await response.json();
        return {
            amount: safeParseNumber(data.amount),
            contacts: data.contacts || 0
        };
    } catch (error) {
        console.error('❌ Failed to calculate monthly usage:', error);
        return { amount: 0, contacts: 0 };
    }
  }, [apiKey]);

  const getPaymentMethods = useCallback(async (): Promise<PaymentMethod[]> => {
    if (!apiKey) return [];

    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/payment-methods`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });

      if (!response.ok) {
        if (response.status === 401) {

          localStorage.setItem('show_login_page', 'true');
          window.location.reload();
          return [];
        }
        throw new Error(`Failed to fetch payment methods: ${response.status}`);
      }
      const data = await response.json();
      return data.payment_methods || [];
    } catch {
      showToast({ type: 'error', title: 'Error', message: 'Failed to load payment methods' });
      return [];
    }
  }, [apiKey, showToast]);

  // Pay-As-You-Go Functions
  const getPayAsYouGoStatus = useCallback(async (): Promise<PayAsYouGoStatus | null> => {
    if (!apiKey) return null;

    try {
      const response = await fetch(`${API_BASE_URL}/api/pay-as-you-go/status`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });

      if (!response.ok) {
        if (response.status === 401) {

          localStorage.setItem('show_login_page', 'true');
          window.location.reload();
          return null;
        }
        throw new Error(`Failed to fetch pay-as-you-go status: ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch pay-as-you-go status';
      showToast({ type: 'error', title: 'Error', message: errorMessage });
      return null;
    }
  }, [apiKey, showToast]);

  const unlockContactsPayAsYouGo = useCallback(async (
    request: PayAsYouGoUnlockRequest
  ): Promise<PayAsYouGoUnlockResponse | null> => {
    if (!apiKey) return null;

    try {
      const response = await fetch(`${API_BASE_URL}/api/pay-as-you-go/unlock-contacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        if (response.status === 401) {

          localStorage.setItem('show_login_page', 'true');
          window.location.reload();
          return null;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to unlock contacts: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Show success message with details
      if (result.success) {
        const message = result.debt_created 
          ? `Unlocked ${result.contacts_unlocked} contacts. Debt created: $${result.debt_created}`
          : `Unlocked ${result.contacts_unlocked} contacts. Charged: $${result.total_charged}`;
        
        showToast({ 
          type: 'success', 
          title: 'Contacts Unlocked', 
          message 
        });
      }
      
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to unlock contacts';
      showToast({ type: 'error', title: 'Unlock Failed', message: errorMessage });
      return null;
    }
  }, [apiKey, showToast]);

  const testBatchCalculation = useCallback(async (
    totalContacts: number,
    currentDebt: number = 0
  ): Promise<PayAsYouGoBatchCalculation | null> => {
    if (!apiKey) return null;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/pay-as-you-go/test-batch-calculation?total_contacts=${totalContacts}&current_debt=${currentDebt}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${apiKey}` }
        }
      );

      if (!response.ok) {
        if (response.status === 401) {

          localStorage.setItem('show_login_page', 'true');
          window.location.reload();
          return null;
        }
        throw new Error(`Failed to calculate batch: ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to calculate batch';
      showToast({ type: 'error', title: 'Error', message: errorMessage });
      return null;
    }
  }, [apiKey, showToast]);

  const deletePaymentMethod = useCallback(async (paymentMethodId: string): Promise<boolean> => {
    if (!apiKey) return false;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/billing/payment-methods/${paymentMethodId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${apiKey}` }
        }
      );

      if (!response.ok) {
        if (response.status === 401) {

          localStorage.setItem('show_login_page', 'true');
          window.location.reload();
          return false;
        }
        throw new Error(`Failed to delete payment method: ${response.status}`);
      }
      
      showToast({ type: 'success', title: 'Success', message: 'Payment method removed' });
      await fetchBalance();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete payment method';
      showToast({ type: 'error', title: 'Error', message: errorMessage });
      return false;
    }
  }, [apiKey, showToast, fetchBalance]);

  const stopBalancePolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const startBalancePolling = useCallback((interval = BILLING_POLL_INTERVAL) => {
    stopBalancePolling();
    fetchBalance();
    pollingIntervalRef.current = setInterval(fetchBalance, interval);
  }, [fetchBalance, stopBalancePolling]);

  // ENHANCED: Auto-start balance polling when authenticated
  useEffect(() => {
    if (apiKey) {
      startBalancePolling(BILLING_POLL_INTERVAL); // Poll every 5 seconds
    } else {
      stopBalancePolling();
    }
    
    return () => stopBalancePolling();
  }, [apiKey, startBalancePolling, stopBalancePolling]);

  const { state } = useAppContext();

  return {
    balance: state.billingBalance,
    loading: state.billingLoading,
    error: state.billingError,
    fetchBalance,
    createSetupIntent,
    confirmSetupIntent,
    toggleAutoCharge,
    getBillingHistory,
    getMonthlyUsage,
    getPaymentMethods,
    deletePaymentMethod,
    startBalancePolling,
    stopBalancePolling,
    // Pay-As-You-Go functions
    getPayAsYouGoStatus,
    unlockContactsPayAsYouGo,
    testBatchCalculation
  };
};